import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { api, useApi } from "../lib/api";
import { Badge, Button, Card, cx, Empty, ErrorNote, Field, inputCls, Modal, Notice, PageHeader, StickyBar, Table, Td, useAction } from "../components/ui";

type Filter = { field: string; op: string; value?: any };
const OP_LABEL: Record<string, string> = { eq: "is", neq: "is not", in: "is any of", contains: "contains", not_contains: "doesn't contain", gte: "≥", lte: "≤", is_true: "is yes", is_false: "is no" };

export default function CampaignBuilder() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  // Contacts → "Add to campaign" arrives with ?contacts=id1,id2 (a hand-picked list instead of filters).
  const picked = useMemo(() => (params.get("contacts") ?? "").split(",").filter(Boolean), [params]);
  const { data: cat } = useApi<any>("/catalog");
  const { data: counts } = useApi<any>("/campaigns/offering-counts");
  const [offering, setOffering] = useState(params.get("offering") ?? "");
  const [description, setDescription] = useState("");
  const [filters, setFilters] = useState<Filter[] | null>(picked.length ? [] : null);
  const [notes, setNotes] = useState<string>("");
  const [preview, setPreview] = useState<any>(null);
  const [decisions, setDecisions] = useState<Record<string, { keep: boolean; reason?: string }>>({});
  const [name, setName] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [showDropped, setShowDropped] = useState(false);
  const [includeOther, setIncludeOther] = useState(false);
  const gen = useAction();
  const prev = useAction();
  const create = useAction();
  const catalog: any[] = cat?.catalog ?? [];

  const runPreview = (f: Filter[], other = includeOther, off = offering) =>
    prev.run(async () => {
      const p = await api("/campaigns/preview", { body: { offering: off, filters: f, includeOtherTerritories: other, contactIds: picked.length ? picked : undefined } });
      setPreview(p);
      // Executives default to Remove (Phase 5.4); the rep opts in with a reason.
      setDecisions(Object.fromEntries(p.flagged.map((x: any) => [x.contactId, { keep: x.flags.every((fl: any) => fl.defaultKeep) }])));
    });
  useEffect(() => {
    if (picked.length && offering) void runPreview([], false, offering);
  }, [offering]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = () =>
    gen.run(async () => {
      const r = await api("/campaigns/filters", { body: { description, offering } });
      setFilters(r.filters);
      setNotes(`${r.source === "claude" ? "Claude" : "Keyword match"}${r.notes ? `: ${r.notes}` : ""}`);
      await runPreview(r.filters);
    });

  const flagged: any[] = preview?.flagged ?? [];
  const undecided = flagged.filter((f) => !decisions[f.contactId]);
  const missingReason = flagged.filter((f) => decisions[f.contactId]?.keep && f.flags.some((x: any) => x.needsReason) && !decisions[f.contactId]?.reason?.trim());
  const keepCount = (preview?.kept.length ?? 0) + flagged.filter((f) => decisions[f.contactId]?.keep).length;
  const ready = !!offering && !!preview && !undecided.length && !missingReason.length && keepCount > 0;

  const submit = () =>
    create.run(async () => {
      const r = await api("/campaigns", { body: { name, offering, description, filters: filters ?? [], decisions, includeOtherTerritories: includeOther, contactIds: picked.length ? picked : undefined } });
      nav(`/campaigns/${r.campaign.id}`);
    });

  return (
    <div className="space-y-5">
      <PageHeader title="New campaign" sub="Describe who you want to reach, pick the offering, check the list, then Claude drafts one sequence for the whole campaign." />

      {picked.length > 0 ? (
        <Notice tone="accent">{picked.length} contact(s) picked from Contacts. Guardrails still apply. <Link className="underline" to="/campaigns/new">Start from filters instead</Link></Notice>
      ) : (
        <Card>
          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-ink"><Sparkles size={15} /> Who do you want to reach?</span>
            <textarea className={cx(inputCls, "text-[0.95rem]")} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. payroll leaders at healthcare accounts in my territory with HR Talent" />
          </label>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button tone="primary" disabled={!description.trim() || !offering || gen.busy} onClick={generate} title={!offering ? "Pick an offering below first" : ""}>
              {gen.busy ? "Finding contacts…" : "Find contacts"}
            </Button>
            <Button tone="ghost" disabled={!offering} onClick={() => { setFilters([]); void runPreview([]); }}>Build filters by hand</Button>
            <span className="text-xs text-muted">Claude turns this into filters. It only sees field names and allowed values, never contact data.</span>
          </div>
          <ErrorNote error={gen.error} />
        </Card>
      )}

      <div>
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-[0.95rem] font-semibold">Offering</h2>
          {counts && <span className="text-xs text-muted">Contacts you could enroll in {counts.scope}</span>}
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(cat?.offerings ?? []).map((o: string) => {
            const kb = cat?.kbStatus?.[o] ?? "none";
            const blocked = kb !== "approved";
            return (
              <button
                key={o}
                disabled={blocked}
                onClick={() => { setOffering(o); setPreview(null); if (filters) void runPreview(filters, includeOther, o); }}
                title={blocked ? "Needs an approved knowledge-base document" : ""}
                className={cx("rounded-xl border bg-surface p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-55", offering === o ? "border-accent ring-1 ring-accent" : "border-line hover:border-accent")}
              >
                <div className="text-sm font-medium text-ink">{o}</div>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className={kb === "approved" ? "text-good" : "text-muted"}>{kb === "approved" ? "Knowledge base approved" : kb === "draft" ? "Knowledge base in draft" : "No knowledge base yet"}</span>
                  <span className="tabular-nums text-muted">{counts?.counts?.[o] ?? "…"} contacts</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {filters && !picked.length && (
        <Card title="Filters" actions={<span className="text-xs text-muted">{notes}</span>}>
          <FilterChips catalog={catalog} filters={filters} onChange={(f) => { setFilters(f); if (offering) void runPreview(f); }} />
        </Card>
      )}

      <ErrorNote error={prev.error} />
      {preview && (
        <Card
          title="Who's in"
          actions={
            preview.myTerritory && !picked.length && (
              <label className="flex items-center gap-2 text-sm text-ink-2">
                <input type="checkbox" checked={includeOther} onChange={(e) => { setIncludeOther(e.target.checked); void runPreview(filters ?? [], e.target.checked); }} /> Include other territories
              </label>
            )
          }
        >
          <div className="mb-3 text-sm text-muted">
            {picked.length ? "Your picked contacts" : preview.myTerritory && !preview.includeOtherTerritories ? `${preview.myTerritory.ownerName}'s territory (${preview.myTerritory.states.length} states)` : "All territories"}
            {!picked.length && preview.otherTerritoryCount > 0 && !preview.includeOtherTerritories && ` · ${preview.otherTerritoryCount} more match in other territories`}
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            <Count label="Matched" n={preview.matched} />
            <Count label="Going in" n={preview.kept.length} />
            <Count label="Your call" n={flagged.length} />
            <Count label="Left out by guardrails" n={preview.dropped.length} onClick={() => setShowDropped(!showDropped)} />
          </div>
          {Object.keys(preview.dropSummary).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {Object.entries(preview.dropSummary).map(([r, n]) => <Badge key={r}>{r}: {String(n)}</Badge>)}
            </div>
          )}
          {showDropped && (
            <div className="mt-3 max-h-72 overflow-auto">
              <Table head={["Contact", "Account", "Territory", "Left out because"]}>
                {preview.dropped.map((d: any) => (
                  <tr key={d.contactId}><Td>{d.name}<div className="text-xs text-muted">{d.title}</div></Td><Td>{d.accountName}</Td><Td className="text-xs">{d.territoryOwner ?? "Unassigned"}</Td><Td className="text-xs">{d.reasons.join("; ")}</Td></tr>
                ))}
              </Table>
            </div>
          )}
          {preview.kept.some((k: any) => k.outOfTerritory) && (
            <div className="mt-4">
              <div className="mb-1 text-sm font-medium">Outside your territory</div>
              <p className="mb-2 text-xs text-muted">Allowed. The territory owner gets a notice before anything sends.</p>
              <Table head={["Contact", "Account", "Territory owner"]}>
                {preview.kept.filter((k: any) => k.outOfTerritory).map((k: any) => <tr key={k.contactId}><Td>{k.name}<div className="text-xs text-muted">{k.title}</div></Td><Td>{k.accountName}</Td><Td>{k.territoryOwner}</Td></tr>)}
              </Table>
            </div>
          )}
        </Card>
      )}

      {preview && flagged.length > 0 && (
        <Card title={`Your call (${flagged.length})`} actions={<><Button tone="ghost" onClick={() => setDecisions(Object.fromEntries(flagged.map((f) => [f.contactId, { keep: false }])))}>Remove all</Button></>}>
          <p className="mb-3 text-sm text-muted">Executives are a high-risk cold open, so they start as Remove. Keeping one needs a reason, which is logged.</p>
          <Table head={["Contact", "Why", "Decision"]}>
            {flagged.map((f) => {
              const d = decisions[f.contactId];
              return (
                <tr key={f.contactId}>
                  <Td><div className="font-medium text-ink">{f.name}</div><div className="text-xs text-muted">{f.title} · {f.accountName}</div>{f.outOfTerritory && <div className="text-xs text-muted">{f.territoryOwner}'s territory</div>}</Td>
                  <Td className="text-xs">{f.flags.map((x: any) => <div key={x.code}>{x.reason}</div>)}</Td>
                  <Td>
                    <div className="flex gap-1">
                      <Button tone={d?.keep ? "primary" : "default"} onClick={() => setDecisions({ ...decisions, [f.contactId]: { keep: true, reason: d?.reason } })}>Keep</Button>
                      <Button tone={d && !d.keep ? "primary" : "default"} onClick={() => setDecisions({ ...decisions, [f.contactId]: { keep: false } })}>Remove</Button>
                    </div>
                    {d?.keep && <input className={cx(inputCls, "mt-1")} placeholder="Reason (required)" value={d.reason ?? ""} onChange={(e) => setDecisions({ ...decisions, [f.contactId]: { keep: true, reason: e.target.value } })} />}
                  </Td>
                </tr>
              );
            })}
          </Table>
        </Card>
      )}

      {preview && (
        <StickyBar>
          <div className="mr-auto w-full max-w-sm">
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder={`Campaign name (optional): ${offering || "offering"} · ${new Date().toISOString().slice(0, 10)}`} aria-label="Campaign name" />
          </div>
          {missingReason.length > 0 && <span className="text-xs text-warn">Kept executives need a reason</span>}
          <Button tone="primary" disabled={!ready || create.busy} onClick={() => setConfirm(true)}>
            Create campaign with {keepCount}
          </Button>
        </StickyBar>
      )}
      <ErrorNote error={create.error} />

      {confirm && (
        <Modal title="Create and draft?" onClose={() => setConfirm(false)} actions={<><Button onClick={() => setConfirm(false)}>Cancel</Button><Button tone="primary" disabled={create.busy} onClick={submit}>{create.busy ? "Creating…" : `Create with ${keepCount}`}</Button></>}>
          <p>{keepCount} contact(s) go into <b>{offering}</b>. Claude drafts one sequence for the whole campaign (5 emails, 2 call scripts, LinkedIn note and message) with merge fields for each contact. You review and approve it once; nothing is sent before that.</p>
        </Modal>
      )}
    </div>
  );
}

function Count({ label, n, onClick }: { label: string; n: number; onClick?: () => void }) {
  return (
    <button onClick={onClick} disabled={!onClick} className="rounded-lg border border-line p-3 text-left disabled:cursor-default">
      <div className="text-xs text-muted">{label}{onClick ? " ▾" : ""}</div>
      <div className="text-xl font-semibold tabular-nums text-ink">{n}</div>
    </button>
  );
}

function FilterChips({ catalog, filters, onChange }: { catalog: any[]; filters: Filter[]; onChange: (f: Filter[]) => void }) {
  const [adding, setAdding] = useState<(Filter & { __i?: number }) | null>(null);
  const label = (f: string) => catalog.find((c) => c.field === f)?.label ?? f;
  const show = (v: any) => (Array.isArray(v) ? v.join(", ") : v === undefined ? "" : String(v));
  const def = useMemo(() => catalog.find((c) => c.field === adding?.field), [adding, catalog]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {!filters.length && <Empty>No filters: everyone in scope matches (guardrails still apply).</Empty>}
        {filters.map((f, i) => (
          <span key={i} className="inline-flex items-center gap-1 rounded-full bg-accent-soft py-1 pl-3 pr-1 text-sm text-accent-text">
            <button className="hover:underline" onClick={() => setAdding({ ...f, __i: i })}>
              <b>{label(f.field)}</b> {OP_LABEL[f.op]} {show(f.value)}
            </button>
            <button aria-label="Remove filter" className="rounded-full p-0.5 hover:bg-surface" onClick={() => onChange(filters.filter((_, j) => j !== i))}>
              <X size={13} />
            </button>
          </span>
        ))}
        <Button tone="ghost" onClick={() => setAdding({ field: catalog[0]?.field ?? "industry", op: catalog[0]?.ops[0] ?? "eq" })}>+ Add filter</Button>
      </div>
      {adding && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-line bg-surface-2 p-3">
          <Field label="Field">
            <select className={inputCls} value={adding.field} onChange={(e) => { const d = catalog.find((c) => c.field === e.target.value); setAdding({ field: e.target.value, op: d?.ops[0] ?? "eq" }); }}>
              {catalog.map((c) => <option key={c.field} value={c.field}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Operator">
            <select className={inputCls} value={adding.op} onChange={(e) => setAdding({ ...adding, op: e.target.value })}>
              {(def?.ops ?? []).map((o: string) => <option key={o} value={o}>{OP_LABEL[o]}</option>)}
            </select>
          </Field>
          {!["is_true", "is_false"].includes(adding.op) && (
            <Field label="Value" help={adding.op === "in" ? "Comma-separated" : undefined}>
              {def?.values && adding.op !== "in" ? (
                <select className={inputCls} value={show(adding.value)} onChange={(e) => setAdding({ ...adding, value: def.type === "number" ? Number(e.target.value) : e.target.value })}>
                  <option value="" />
                  {def.values.map((v: any) => <option key={v} value={v}>{v}</option>)}
                </select>
              ) : (
                <input className={inputCls} list={`vals-${adding.field}`} value={show(adding.value)} onChange={(e) => setAdding({ ...adding, value: adding.op === "in" ? e.target.value.split(",").map((s) => s.trim()) : def?.type === "number" ? Number(e.target.value) : e.target.value })} />
              )}
              <datalist id={`vals-${adding.field}`}>{(def?.values ?? []).map((v: any) => <option key={v} value={v} />)}</datalist>
            </Field>
          )}
          <Button
            tone="primary"
            onClick={() => {
              const { __i, ...f } = adding;
              onChange(__i === undefined ? [...filters, f] : filters.map((x, j) => (j === __i ? f : x)));
              setAdding(null);
            }}
          >
            {adding.__i === undefined ? "Add" : "Update"}
          </Button>
          <Button tone="ghost" onClick={() => setAdding(null)}>Cancel</Button>
        </div>
      )}
    </div>
  );
}
